import React from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { HiArrowNarrowRight } from "react-icons/hi";
import {
  HiChevronRight,
  HiOutlineBuildingOffice2,
  HiOutlineEye,
  HiOutlineSquares2X2,
  HiOutlineUserPlus,
  HiOutlineUsers,
} from "react-icons/hi2";

const BrandContent = () => {
  const location = useLocation();
  const isOverviewPage = location.pathname === "/company/dashboard/brands";
  const setupSteps = [
    {
      title: "Create Brands",
      description:
        "Configure the commission plan for your affiliates, invite affiliate in less than 5 minutes to get started.",
      ctaLabel: "Create",
      ctaPath: "/company/dashboard/brands/create",
      icon: HiOutlineBuildingOffice2,
    },
    {
      title: "View Brands",
      description:
        "View and manage all your brands in one place. Track their status, performance, and manage their accounts.",
      ctaLabel: "View",
      ctaPath: "/company/dashboard/brands/",
      icon: HiOutlineEye,
    },
    {
      title: "Brand Dashboard",
      description:
        "Fuel your affiliate program and start inviting your affiliate partners to maximize your reach and growth.",
      ctaLabel: "Dashboard",
      ctaPath: "/company/dashboard/brands",
      icon: HiOutlineSquares2X2,
    },
    {
      title: "Add Brand Manager's",
      description:
        "Advertise your brand with brand manager's and start inviting your affiliate partners to maximize your reach and growth.",
      ctaLabel: "Add",
      ctaPath: "/company/dashboard/brands/brand-managers",
      icon: HiOutlineUserPlus,
    },
    {
      title: "View Brand Manager's",
      description:
        "View and manage all your brand manager's in one place. Track their status, performance, and manage their accounts.",
      ctaLabel: "View",
      ctaPath: "/company/dashboard/brands/brand-managers/dashboard",
      icon: HiOutlineUsers,
    },
  ];

  // Helper function to truncate content after 20 words
  const truncateText = (text, wordLimit) => {
    const words = text.split(" ");
    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(" ") + "...";
    }
    return text;
  };

  if (!isOverviewPage) {
    return <Outlet />;
  }

  return (
    <div className="bg-gray-100 min-h-full w-full flex flex-col">
      <div className="flex flex-col lg:flex-row flex-1">
        <div className="flex-1 p-0 md:p-3 lg:p-4">
          <div className="bg-white border border-gray-100 shadow-sm p-2 mb-4 md:hidden">
            <h2 className="text-lg leading-7 font-bold mb-2">Brand Home</h2>
            <p className="text-xs text-gray-500 mb-4">
              You're literally minutes away from launching your partnership
              marketing program.
            </p>

            <div className="flex items-center justify-between mb-4">
              {setupSteps.map((_, index) => (
                <React.Fragment key={`brand-mobile-step-${index + 1}`}>
                  <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[11px] font-semibold flex items-center justify-center">
                    {index + 1}
                  </span>
                  {index < setupSteps.length - 1 && (
                    <HiArrowNarrowRight className="text-orange-500 text-lg shrink-0" />
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="space-y-3">
              {setupSteps.map((step) => {
                const StepIcon = step.icon;

                return (
                  <div
                    key={step.title}
                    className="rounded-xl border border-gray-200 bg-white p-3 shadow-[0_1px_2px_rgba(16,24,40,0.06)]"
                  >
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 rounded-full bg-orange-50 text-orange-500 flex items-center justify-center shrink-0">
                        <StepIcon className="text-lg" />
                      </span>
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-lg md:text-xl font-semibold leading-5">
                            {step.title}
                          </h3>
                          <HiChevronRight className="text-gray-300 text-lg shrink-0 mt-0.5" />
                        </div>
                        <p className="text-[10px] text-gray-500 mt-1.5 leading-4">
                          {truncateText(step.description, 18)}
                        </p>
                        <div className="mt-3 flex justify-end">
                          <Link
                            to={step.ctaPath}
                            className="inline-flex items-center justify-center rounded-md bg-orange-500 px-3 py-1.5 text-[11px] font-semibold text-white"
                          >
                            {step.ctaLabel}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="hidden md:block bg-white shadow-md rounded-lg p-4 lg:p-5 mb-10 lg:mb-0 ">
            <h2 className="text-lg font-bold mb-2">Brand Home</h2>
            <p className="text-[10px] md:text-sm text-gray-500 mb-5">
              You're literally minutes away from launching your partnership
              marketing program.
            </p>

            {/* Setup Steps */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Step 1 */}
              <div className="p-5 border rounded-lg flex flex-col h-full">
                <h3 className="font-semibold mb-2">Create Brands</h3>
                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "Configure the commission plan for your affiliates, invite affiliate in less than 5 minutes to get started.",
                    20
                  )}
                </p>
                <Link
                  to="/company/dashboard/brands/create"
                  className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto"
                >
                  Create
                </Link>
              </div>

              {/* Step 2 */}

              <div className="p-5 border rounded-lg flex flex-col h-full">
                <h3 className="font-semibold mb-2">View Brands</h3>
                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "View and manage all your brands in one place. Track their status, performance, and manage their accounts.",
                    20
                  )}
                </p>
                <Link
                  to="/company/dashboard/brands/"
                  className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto"
                >
                  View
                </Link>
              </div>

              {/* Step 3 */}
              <div className="p-5 border rounded-lg flex flex-col h-full">
                <h3 className="font-semibold mb-2">Brand Dashboard</h3>
                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "Fuel your affiliate program and start inviting your affiliate partners to maximize your reach and growth.",
                    20
                  )}
                </p>
                <Link
                  to="/company/dashboard/brands"
                  className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto"
                >
                  Dashboard
                </Link>
              </div>


              {/* Step 4 */}
              <div className="p-5 border rounded-lg flex flex-col h-full">
                <h3 className="font-semibold mb-2">Add Brand Manager's</h3>
                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "Advertise your brand with brand manager's and start inviting your affiliate partners to maximize your reach and growth.",
                    20
                  )}
                </p>
                <Link
                  to="/company/dashboard/brands/brand-managers"
                  className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto"
                >
                  Add
                </Link>
              </div>


              {/* Step 5 */}
              <div className="p-5 border rounded-lg flex flex-col h-full">
                <h3 className="font-semibold mb-2">View Brand Manager's</h3>
                <p className="text-[10px] md:text-sm text-gray-500 mb-4 flex-1">
                  {truncateText(
                    "View and manage all your brand manager's in one place. Track their status, performance, and manage their accounts.",
                    20
                  )}
                </p>
                <Link
                  to="/company/dashboard/brands/brand-managers/dashboard"
                  className="bg-orange-500 text-[10px] md:text-sm text-white text-center font-medium px-4 py-2 rounded-lg w-full mt-auto"
                >
                  View
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandContent;
